// Portfolio Diversification Checker -- the holdings input form.
//
// Pure UI component (mirrors the ETF Wealth Builder CalculatorForm): owns only
// its local form state and delegates the network call upward via `onSubmit`.

import React, { useMemo, useState } from 'react';

import { useDataAwareLookback } from '../../../shared/hooks/useDataAwareLookback';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { SymbolAutocomplete } from '../../../shared/ui/SymbolAutocomplete';
import { formatHistorySpan, toCoverageTickers } from '../../../shared/utils/lookback';
import { useDiversificationCoverage } from '../api/queries';
import type { HoldingRow, PortfolioRequest } from '../types';

interface PortfolioFormProps {
    onSubmit: (data: PortfolioRequest) => void;
    isPending: boolean;
    error: Error | null;
}

const MIN_HOLDINGS = 2;
const MAX_HOLDINGS = 25;
const TICKER_RE = /^[A-Z0-9^][A-Z0-9.\-=]{0,19}$/;
// Mirrors backend STANDARD_LOOKBACK_OPTIONS (lookback_years 1..10).
const STANDARD_LOOKBACK_OPTIONS: readonly number[] = [1, 2, 3, 5, 10];

const PRESETS: { label: string; holdings: [string, number][] }[] = [
    {
        label: 'Tech-Heavy',
        holdings: [['NVDA', 30], ['MSFT', 25], ['GOOGL', 20], ['AAPL', 15], ['AVGO', 10]],
    },
    { label: 'Classic 60/40', holdings: [['SPY', 60], ['AGG', 40]] },
    {
        label: 'All-Weather',
        holdings: [['SPY', 30], ['TLT', 40], ['IEF', 15], ['GLD', 7.5], ['DBC', 7.5]],
    },
    {
        label: 'Global UCITS',
        holdings: [['VWCE.DE', 50], ['EUNL.DE', 20], ['SXR8.DE', 20], ['AGGH', 10]],
    },
];

let nextId = 1;
const toRows = (holdings: [string, number][]): HoldingRow[] =>
    holdings.map(([ticker, weight]) => ({ id: nextId++, ticker, weight }));

const inputClass =
    'w-full px-3.5 py-3 bg-slate-50 border border-slate-200 rounded-lg text-base text-slate-900 placeholder:text-slate-400 placeholder:normal-case [color-scheme:light] outline-none transition-colors focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/40';

// A few rotating ticker examples so every empty row shows a concrete, valid hint.
const TICKER_PLACEHOLDERS = ['e.g. AAPL', 'e.g. VWCE.DE', 'e.g. SPY', 'e.g. BTCUSD', 'e.g. SAP.DE'];

export const PortfolioForm: React.FC<PortfolioFormProps> = ({ onSubmit, isPending, error }) => {
    const [rows, setRows] = useState<HoldingRow[]>(() => toRows(PRESETS[0].holdings));
    const [localError, setLocalError] = useState<string | null>(null);

    const totalWeight = useMemo(
        () => rows.reduce((sum, r) => sum + (Number.isFinite(r.weight) ? r.weight : 0), 0),
        [rows],
    );

    // Probe the price store for how much overlapping history these tickers have
    // so the Lookback dropdown only offers windows we can actually serve.
    const validTickers = useMemo(
        () => toCoverageTickers(rows.map((r) => r.ticker), TICKER_RE),
        [rows],
    );
    const debouncedTickers = useDebounce(validTickers, 400);
    const { data: coverage } = useDiversificationCoverage(debouncedTickers);
    const { lookbackYears, setLookbackYears, lookbackOptions } = useDataAwareLookback(
        STANDARD_LOOKBACK_OPTIONS,
        coverage,
        3,
    );

    const updateRow = (id: number, patch: Partial<HoldingRow>) =>
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

    const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));

    const addRow = () =>
        setRows((prev) =>
            prev.length >= MAX_HOLDINGS ? prev : [...prev, { id: nextId++, ticker: '', weight: 10 }],
        );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const weights: Record<string, number> = {};
        for (const row of rows) {
            const ticker = row.ticker.trim().toUpperCase();
            if (!ticker) continue;
            if (!TICKER_RE.test(ticker)) {
                setLocalError(`"${row.ticker}" is not a valid ticker symbol.`);
                return;
            }
            if (!(row.weight > 0)) {
                setLocalError(`Weight for ${ticker} must be greater than 0.`);
                return;
            }
            weights[ticker] = (weights[ticker] ?? 0) + row.weight;
        }
        const count = Object.keys(weights).length;
        if (count < MIN_HOLDINGS || count > MAX_HOLDINGS) {
            setLocalError(`Please enter between ${MIN_HOLDINGS} and ${MAX_HOLDINGS} distinct holdings.`);
            return;
        }
        setLocalError(null);
        onSubmit({ weights, lookback_years: lookbackYears });
    };

    const shownError = localError ?? error?.message ?? null;
    const isBalanced = Math.abs(totalWeight - 100) < 0.01;

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-5"
        >
            <div>
                <p className="block text-sm font-semibold text-slate-700 mb-2">Quick-start examples</p>
                <div className="flex flex-wrap gap-2">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            onClick={() => setRows(toRows(preset.holdings))}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-200 bg-slate-50 text-slate-700 hover:border-sky-400 hover:text-sky-700 transition-colors"
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Your Holdings</label>
                <div className="flex gap-2 items-center px-1 mb-1.5">
                    <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Ticker / Symbol
                    </span>
                    <span className="w-28 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Weight (%)
                    </span>
                    <span className="w-10 shrink-0" aria-hidden="true"></span>
                </div>
                <div className="space-y-2.5">
                    {rows.map((row, index) => (
                        <div key={row.id} className="flex gap-2 items-center">
                            <SymbolAutocomplete
                                value={row.ticker}
                                onChange={(next) => updateRow(row.id, { ticker: next })}
                                accent="sky"
                                placeholder={TICKER_PLACEHOLDERS[index % TICKER_PLACEHOLDERS.length]}
                                ariaLabel={`Ticker for holding ${index + 1}`}
                                maxLength={20}
                                inputClassName={`${inputClass} font-semibold tracking-wide uppercase`}
                                listboxId={`pdc-symbols-${row.id}`}
                            />
                            <div className="relative w-28 shrink-0">
                                <input
                                    type="number"
                                    value={Number.isFinite(row.weight) ? row.weight : ''}
                                    onChange={(e) => updateRow(row.id, { weight: Number(e.target.value) })}
                                    min="0"
                                    step="any"
                                    inputMode="decimal"
                                    aria-label={`Weight in percent for holding ${index + 1}`}
                                    className={`${inputClass} pr-7 text-right`}
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-slate-400">
                                    %
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeRow(row.id)}
                                disabled={rows.length <= MIN_HOLDINGS}
                                aria-label={`Remove ${row.ticker || `holding ${index + 1}`}`}
                                className="shrink-0 w-10 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors"
                            >
                                <i className="bx bx-x text-xl" aria-hidden="true"></i>
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between mt-3">
                    <button
                        type="button"
                        onClick={addRow}
                        disabled={rows.length >= MAX_HOLDINGS}
                        className="text-sm font-semibold text-sky-600 hover:text-sky-700 disabled:opacity-40"
                    >
                        + Add holding
                    </button>
                    <span
                        className={`text-xs font-bold px-2 py-1 rounded ${
                            isBalanced ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                        }`}
                    >
                        Total: {totalWeight.toFixed(1)}%
                    </span>
                </div>
                {!isBalanced && (
                    <p className="text-xs text-slate-500 mt-2">
                        Weights are automatically rescaled to 100%.
                    </p>
                )}
                <p className="text-xs text-slate-400 mt-2">
                    Stocks, ETFs and crypto supported, e.g. AAPL, VWCE.DE, SAP.DE, BTCUSD.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Historical Lookback Window
                </label>
                <select
                    value={lookbackYears}
                    onChange={(e) => setLookbackYears(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 [color-scheme:light] outline-none focus:ring-2 focus:ring-sky-500"
                >
                    {lookbackOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                {coverage && coverage.max_lookback_years > 0 && (
                    <p className="mt-1.5 text-xs text-slate-400">
                        {formatHistorySpan(coverage.max_lookback_years)} of shared history for these tickers.
                    </p>
                )}
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-70 flex justify-center items-center"
            >
                {isPending ? 'Running Quant Engines...' : 'Check My Diversification'}
            </button>

            {shownError && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm border border-red-100 mt-2 font-medium">
                    {shownError}
                </div>
            )}
        </form>
    );
};
